import { useSyncExternalStore } from "react";

interface CartLoadingState {
  isGlobalCartLoading: boolean;
  startLoading: (id: string) => void;
  stopLoading: (id: string) => void;
}

type InternalState = {
  isGlobalCartLoading: boolean;
};

let timeoutId: ReturnType<typeof setTimeout> | null = null;
const activeLoadingOperations = new Set<string>();

let internalState: InternalState = { isGlobalCartLoading: false };
const listeners = new Set<() => void>();

function notify() {
  for (const l of Array.from(listeners)) l();
}

function startLoading(id: string) {
  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }

  activeLoadingOperations.add(id);

  if (activeLoadingOperations.size > 0 && !internalState.isGlobalCartLoading) {
    internalState = { isGlobalCartLoading: true };
    notify();
  }
}

function stopLoading(id: string) {
  activeLoadingOperations.delete(id);

  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }

  if (activeLoadingOperations.size === 0) {
    timeoutId = setTimeout(() => {
      if (activeLoadingOperations.size === 0) {
        internalState = { isGlobalCartLoading: false };
        notify();
      }
      timeoutId = null;
    }, 500);
  }
}

export const cartLoadingStore = {
  getState: () => ({ ...internalState, startLoading, stopLoading }),
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  startLoading,
  stopLoading,
};

export function useCartLoadingStore(selector?: (s: CartLoadingState) => any) {
  const subscribe = (cb: () => void) => {
    listeners.add(cb);
    return () => listeners.delete(cb);
  };

  const getSnapshot = () => {
    const s = cartLoadingStore.getState();
    return selector ? selector(s) : s;
  };

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export default useCartLoadingStore;

